<template>
  <div class="file-uploader-wrap">
    <el-upload
      ref="uploader"
      class="file-uploader"
      drag
      :name="uploadName"
      :http-request="httpRequest"
      :action="uploadUrl"
      :data="uploadParams"
      :auto-upload="autoUpload"
      :show-file-list="showFileList && defaultFileList && !$scopedSlots.fileList"
      :file-list="fileList"
      :multiple="!singleFile"
      :accept="accepts"
      :limit="limit"
      :disabled="disabled"
      :before-upload="beforeUpload"
      :on-progress="onProgress"
      :on-success="onSuccess"
      :on-error="onError"
      :on-exceed="onExceed"
      :on-change="onChange">
      <slot name="trigger">
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将文件拖到此处，或<em> 点击上传</em></div>
        <div class="el-upload__tip" slot="tip">
          <div>
            格式支持：
            <span class="theme-text">
              <span v-if="supportTypes && supportTypes.length">{{supportTypes.join('，')}}</span>
              <span v-else>不限制</span>
            </span>
          </div>
          <div>
            大小限制：
            <span class="theme-text">
              <span v-if="maxSize > 0">不超过 {{maxSize | formatFileSize}}</span>
              <span v-else>不限制</span>
            </span>
          </div>
        </div>
      </slot>
    </el-upload>
    <slot v-if="showFileList && !defaultFileList && $scopedSlots.fileList"
          name="fileList"
          :file-list="uploadFileList"
          :remove="removeFile"></slot>
  </div>
</template>

<script>
import Support from '../../../mixins/support'
import ElUpload from '../../../mixins/upload/ElUpload'
export default {
  name: 'FileUploader',
  mixins: [Support, ElUpload],
  props: {
    uploadName: {
      type: String,
      default: 'uploadFile'
    },
    uploadUrl: {
      type: String,
      required: true
    },
    uploadParams: Object,
    autoUpload: {
      type: Boolean,
      default: true
    },
    showFileList: {
      type: Boolean,
      default: true
    },
    defaultFileList: {
      type: Boolean,
      default: true
    },
    fileList: {
      type: Array,
      default: function () {
        return []
      }
    },
    singleFile: Boolean,
    supportTypes: Array,
    maxSize: {
      type: Number,
      default: -1
    },
    maxCount: Number,
    disabled: Boolean
  },
  data () {
    return {
      uploadFileList: []
    }
  },
  computed: {
    accepts () {
      if (this.supportTypes && this.supportTypes.length) {
        return this.supportTypes.join(',')
      }
    },
    limit () {
      if (!this.singleFile) {
        return this.maxCount
      }
    }
  },
  methods: {
    initFileList (postEvent) {
      this.$nextTick(() => {
        let uploader = this.$refs.uploader
        if (uploader) {
          this.uploadFileList = uploader.uploadFiles || []
          if (postEvent) {
            this.$emit('upload-change', this.uploadFileList)
          }
        }
      })
    },
    validateFile (file, showMsg) {
      const supportTypes = this.supportTypes
      if (supportTypes && supportTypes.length) {
        let filename = file.name
        let index = filename.lastIndexOf('.')
        if (index > 0) {
          let suffix = filename.substring(index).toLowerCase()
          if (!supportTypes.some(types => types.toLowerCase() === suffix)) {
            if (showMsg) {
              this.$message.error('文件格式不合法')
            }
            return false
          }
        } else {
          if (showMsg) {
            this.$message.error('文件格式不合法')
          }
          return false
        }
      }
      const maxSize = this.maxSize
      if (maxSize > 0) {
        let isLt = file.size <= maxSize
        if (!isLt) {
          if (showMsg) {
            this.$message.error('文件大小超过限制')
          }
          return false
        }
      }
      return true
    },
    removeFile (file) {
      let uploader = this.$refs.uploader
      if (uploader) {
        uploader.handleRemove(file)
        this.initFileList(true)
      }
    },
    clearFiles () {
      let uploader = this.$refs.uploader
      if (uploader) {
        uploader.clearFiles()
        this.initFileList(true)
      }
    },
    beforeUpload (file) {
      if (this.validateFile(file, true)) {
        this.$emit('on-start', {file})
        return true
      } else {
        return false
      }
    },
    onProgress (event, file) {
      this.$emit('on-progress', {
        file,
        progress: Math.ceil(event.percent)
      })
    },
    onSuccess (res, file) {
      switch (res['responseCode']) {
        case 'SUCCESS': {
          this.$emit('on-success', {
            file,
            result: res
          })
          this.onComplete(file)
          break
        }
        case 'FAILURE': {
          this.$emit('on-error', {
            file,
            error: new Error(res['message'] || '上传失败')
          })
          this.onComplete(file)
          break
        }
        default:
          break
      }
    },
    onError (err, file) {
      this.$emit('on-error', {
        file,
        error: err
      })
      this.onComplete(file)
    },
    onComplete (file) {
      this.$emit('on-complete', {file})
    },
    onExceed () {
      this.$message.error('上传数量超过限制')
    },
    onChange (file, fileList) {
      if (this.singleFile && file.status === 'ready') {
        if (this.validateFile(file, true)) {
          fileList.forEach(f => {
            if (f !== file) {
              this.removeFile(f)
            }
          })
        } else {
          this.clearFiles()
        }
      }
      this.initFileList(true)
    }
  },
  watch: {
    fileList: {
      handler: function () {
        this.initFileList()
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss">
  .file-uploader-wrap {
    .file-uploader {
      padding: 10px;
      .el-upload {
        display: block;
        width: 100%;
        .el-upload-dragger {
          border-radius: 0;
          width: 100%;
          height: 154px;
          .el-icon-upload {
            margin: 30px 0 10px;
            font-size: 50px;
          }
          .el-upload__text {
            font-size: 12px;
          }
        }
      }
      .el-upload__tip {
        color: #909399;
        font-size: 12px;
        line-height: 24px;
        background-color: #fff;
        border: 1px solid #eee;
        padding: 5px 10px;
      }
      .el-upload-list {
        &.el-upload-list--text {
          margin-top: 10px;
          &:empty {
            margin-top: 0;
          }
          .el-upload-list__item {
            padding-left: 5px;
            line-height: 32px;
            border: 1px solid #dcdfe6;
            background-color: #fff;
            box-sizing: border-box;
            &:first-child {
              margin-top: 0;
            }
            .el-upload-list__item-name {
              font-size: 12px;
            }
            .el-upload-list__item-status-label {
              right: 8px;
            }
            .el-icon-close {
              top: 0;
              right: 8px;
              line-height: 32px;
            }
            .el-progress {
              width: auto;
              left: 5px;
              right: 5px;
            }
          }
        }
      }
    }
  }
</style>
